import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
// Custom components
import React, { useEffect, useMemo, useRef } from 'react';
import { useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import { maskCurrency } from 'utils/number';
import MiniStatistics from './card/MiniStatistics';

// Já carregada pelo app (assets/css/App.css), hoje usada só na área de Contratos.
const MONO_STACK = '"JetBrains Mono", "SF Mono", Menlo, monospace';

/**
 * `numeric` marca colunas de dinheiro e quantidade: elas alinham à direita e ganham a mono
 * tabular, para que as casas decimais empilhem entre as linhas.
 *
 * Existia antes um `align: 'right'` nas colunas, mas nada lia a prop — e por isso ela
 * apodreceu: em Saques estava em "Tipo" e "Status" (texto e badge) e faltava justamente na
 * "Taxa". `numeric` nomeia a intenção em vez do efeito visual; `align` segue valendo para
 * alinhamento puro.
 */
const isNumeric = (column: any) => column?.numeric === true;

const alignOf = (column: any): 'left' | 'right' | 'center' => {
  if (column?.id === 'actions') return 'center';
  if (isNumeric(column)) return 'right';
  return column?.align === 'center' ? 'center' : column?.align === 'right' ? 'right' : 'left';
};

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }: any, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || (defaultRef as any);

  React.useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <>
      <Checkbox ref={resolvedRef} {...rest} />
    </>
  );
});

export default function TableList(props: {
  columnsData: any[];
  itensData: any[];
  actions: any;
  pageCount?: number;
  setFilter: any;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: { id: string; desc?: boolean }[];
  enableSelect?: boolean;
  setSelectedRows?: any;
  aggregateSelection?: {
    key: string;
    label: string;
  }[];
}) {
  const {
    columnsData,
    itensData,
    actions,
    pageCount,
    setFilter,
    pageIndex: controlledPageIndex = 0,
    pageSize: controlledPageSize = 20,
    orderBy: controlledOrderBy = [],
    enableSelect,
    setSelectedRows,
    aggregateSelection,
  } = props;

  const columns = useMemo(() => columnsData, [columnsData]);
  const tableData = useMemo(() => itensData, [itensData]);

  const tableInstance = useTable<{ id: string }>(
    {
      columns,
      manualPagination: true,
      manualSortBy: true,
      initialState: {
        pageIndex: controlledPageIndex,
        pageSize: controlledPageSize,
        sortBy: controlledOrderBy,
      },
      data: tableData || [],
      autoResetPage: false,
      pageCount: pageCount || 0,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      enableSelect &&
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: 'selection',
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row, ...props }: any) => {
              return (
                <div>
                  <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} isChecked={row.isSelected} select />
                </div>
              );
            },
          },
          ...columns,
        ]);
    },
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // Get the state from the instance
    state: { pageIndex, pageSize, sortBy, selectedRowIds },
  } = tableInstance;

  const prevControlledPageIndex = useRef(controlledPageIndex);
  const prevControlledPageSize = useRef(controlledPageSize);

  useEffect(() => {
    if (prevControlledPageIndex.current !== controlledPageIndex) {
      prevControlledPageIndex.current = controlledPageIndex;
      if (pageIndex !== controlledPageIndex) {
        gotoPage(controlledPageIndex);
      }
    }
  }, [controlledPageIndex, gotoPage, pageIndex]);

  useEffect(() => {
    if (prevControlledPageSize.current !== controlledPageSize) {
      prevControlledPageSize.current = controlledPageSize;
      if (pageSize !== controlledPageSize) {
        setPageSize(controlledPageSize);
      }
    }
  }, [controlledPageSize, setPageSize, pageSize]);

  useEffect(() => {
    setFilter((prev: any) => {
      const prevOrderBy = JSON.stringify(prev.orderBy ?? []);
      const nextOrderBy = JSON.stringify(sortBy ?? []);
      if (
        prev.pageIndex === pageIndex &&
        prev.pageSize === pageSize &&
        prevOrderBy === nextOrderBy
      ) {
        return prev;
      }
      prevControlledPageIndex.current = pageIndex;
      prevControlledPageSize.current = pageSize;
      return { ...prev, pageIndex, pageSize, orderBy: sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, sortBy]);

  useEffect(() => {
    setSelectedRows &&
      setSelectedRows(
        tableData
          .map((item: any, i) => {
            if (selectedRowIds[i]) {
              return item.id;
            }
            return null;
          })
          .filter((item: any) => item !== null),
      );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowIds]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const selectedCount = Object.keys(selectedRowIds ?? {}).length;
  const hoverBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  return (
    <>
      <Box overflowY="auto">
        {/* Ocupava uma faixa inteira com "R$ 0,00" mesmo sem nada marcado. */}
        <HStack px={5} pb={selectedCount > 0 ? 3 : 0}>
          {selectedCount > 0 &&
            aggregateSelection?.map(({ key, label }) => (
              <MiniStatistics
                key={key}
                name={`${label} (${selectedCount})`}
                value={maskCurrency(
                  tableData
                    .map((item: any, i) => (selectedRowIds[i] ? item : null))
                    .filter((item: any) => item !== null)
                    .reduce((acc, cur) => acc + Math.abs(+cur[key]), 0) || 0,
                )}
              />
            ))}
        </HStack>
        {tableData?.length === 0 && (
          <Flex justify="center" align="center" h="100%" w="100%" minH={{ sm: '100px', lg: '200px' }} color="gray.500">
            <Text>Nenhum registro encontrado</Text>
          </Flex>
        )}
        {/* Era `variant="striped"`: a zebra impunha um ritmo que não diz nada sobre os dados.
            Um filete entre as linhas basta para guiar o olho na horizontal. */}
        {tableData?.length > 0 && (
          <Table {...getTableProps()} variant="unstyled" color="gray.500" mb="24px" overflow="hidden">
            <Thead>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      pe="10px"
                      key={index}
                      borderColor={borderColor}
                      // O cabeçalho precisa acompanhar o alinhamento da célula, senão o
                      // rótulo de dinheiro fica na esquerda e o número na direita.
                      textAlign={alignOf(column)}
                    >
                      <Flex
                        justify={alignOf(column) === 'right' ? 'flex-end' : 'space-between'}
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.500"
                      >
                        {column.render('Header') as string}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <Tr
                    {...row.getRowProps()}
                    key={index}
                    borderTopWidth="1px"
                    borderColor={borderColor}
                    _hover={{ bg: hoverBg }}
                    transition="background-color 0.12s ease"
                  >
                    {row.cells.map((cell, index) => {
                      const align = alignOf(cell.column);
                      const numeric = isNumeric(cell.column);
                      let data;
                      if (cell.column.id === 'actions') {
                        data = actions(cell.row.original);
                      } else {
                        data = (
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight={numeric ? '500' : '400'}
                            // Dinheiro em mono tabular: é o que faz R$ 14.993,10 e R$ 400,00
                            // empilharem vírgula com vírgula. A fonte já é carregada pelo app.
                            fontFamily={numeric ? MONO_STACK : undefined}
                            // O body tem letterSpacing -0.5px global; em dígitos de 12px isso
                            // aperta demais.
                            letterSpacing={numeric ? 'normal' : undefined}
                            sx={numeric ? { fontVariantNumeric: 'tabular-nums' } : undefined}
                          >
                            {cell.render('Cell') as string}
                          </Text>
                        );
                      }

                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          py={2}
                          px={5}
                          borderBottom="none"
                          textAlign={align}
                          fontSize={{ sm: '12px' }}
                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>
      <Flex width="100%" textAlign="center" alignItems={'center'} justifyContent="center">
        <Flex
          alignItems="center"
          fontWeight="normal"
          direction={{
            base: 'column',
            md: 'row',
          }}
        >
          <HStack
            spacing={3}
            mb={{
              base: 3,
              md: 0,
            }}
            mr={{
              base: 0,
              md: 3,
            }}
          >
            <Button
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              size="xs"
              variant="outline"
              borderRadius="sm"
            >
              {'<<'}
            </Button>
            <Button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              size="xs"
              variant="outline"
              borderRadius="sm"
            >
              {'<'}
            </Button>
            <Button onClick={() => nextPage()} disabled={!canNextPage} size="xs" variant="outline" borderRadius="sm">
              {'>'}
            </Button>
            <Button
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
              size="xs"
              variant="outline"
              borderRadius="sm"
            >
              {'>>'}
            </Button>
          </HStack>
          <HStack spacing={3}>
            <span>
              Página{` `}
              <strong>
                {pageIndex + 1} de {pageOptions.length}
              </strong>
            </span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[20, 50, 100, 250, 500].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Exibir {pageSize}
                </option>
              ))}
            </select>
          </HStack>
        </Flex>
      </Flex>
    </>
  );
}
